/**
 * Lazy, pause-when-offscreen Lottie for the decorative tap hints.
 *
 * These are two small "tap here" animations far below the fold on the NexDesk
 * PDP. They were rendered as <lottie-player loop autoplay> with the 250KB
 * player pulled from unpkg by two separate sections — so the script was
 * fetched twice, ran during page load, and then drove requestAnimationFrame
 * forever. Lighthouse measured 13.6s of main-thread time attributed to it,
 * which is most of the page's 8.2s blocking time and why it never reached idle.
 *
 * Now: nothing loads until a hint is about to enter the viewport, the player is
 * fetched once, and each animation plays only while it is actually on screen.
 * Honours prefers-reduced-motion by never loading at all.
 */
(function () {
  var PLAYER_SRC = 'https://unpkg.com/@lottiefiles/lottie-player@2.0.12/dist/lottie-player.js';
  var placeholders = document.querySelectorAll('[data-lottie-tap]');
  if (!placeholders.length) return;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var playerPromise = null;
  function loadPlayer() {
    if (playerPromise) return playerPromise;
    playerPromise = new Promise(function (resolve, reject) {
      if (window.customElements && window.customElements.get('lottie-player')) return resolve();
      var existing = document.querySelector('script[data-lottie-player]');
      if (existing) {
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = PLAYER_SRC;
      script.async = true;
      script.setAttribute('data-lottie-player', '');
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return playerPromise;
  }

  function upgrade(placeholder) {
    if (placeholder.dataset.lottieReady) return Promise.resolve(null);
    placeholder.dataset.lottieReady = '1';
    return loadPlayer().then(function () {
      var player = document.createElement('lottie-player');
      player.setAttribute('src', placeholder.dataset.lottieTap);
      player.setAttribute('background', 'transparent');
      player.setAttribute('speed', '1');
      player.setAttribute('loop', '');
      player.className = placeholder.className;
      player.style.cssText = placeholder.style.cssText;
      placeholder.replaceWith(player);
      return player;
    }).catch(function () { return null; });
  }

  // Start fetching a little before the hint is on screen so it is ready in time,
  // but only play while it is genuinely visible.
  var loader = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var placeholder = entry.target;
      loader.unobserve(placeholder);
      upgrade(placeholder).then(function (player) {
        if (player) playback.observe(player);
      });
    });
  }, { rootMargin: '300px' });

  var playback = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var player = entry.target;
      if (typeof player.play !== 'function') return;
      if (entry.isIntersecting) player.play();
      else if (typeof player.pause === 'function') player.pause();
    });
  }, { threshold: 0.01 });

  placeholders.forEach(function (placeholder) { loader.observe(placeholder); });
})();
